/**
 * 📸 CameraCapture — a compact modal camera used ONLY for capturing a
 * snapshot of gear / food / a room corner.
 *
 * Feb 2026 architectural pivot (owner directive):
 *   "The camera is meant to add what the user has to mimic it in the app.
 *    The AR would be great for other areas but not the kitchen or gym
 *    unless its capturing equipment or items for the avatar to use."
 *
 * This replaces the old fullscreen live-camera "AR shell" for the
 * kitchen/gym pages. The camera is now a transient tool the user
 * opens on demand, snaps a photo, and closes. The kitchen/gym pages
 * themselves are normal teaching surfaces — the avatar coaches
 * inside the app, not overlaid on a Pokémon-Go camera feed.
 *
 * Usage:
 *   <CameraCapture
 *     open={boolean}
 *     title="Snap your fridge"
 *     hint="Point at the whole shelf — the AI will list what it sees"
 *     onCapture={(dataUrlJpeg) => …}
 *     onClose={() => …}
 *   />
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, X, RefreshCw, Loader2, AlertCircle } from "lucide-react";

const CONSTRAINT_TIERS = [
  { video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false },
  { video: { facingMode: { ideal: "environment" } }, audio: false },
  { video: { facingMode: { ideal: "user" } }, audio: false },
  { video: true, audio: false },
];

export function CameraCapture({ open, title, hint, onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | requesting | live | denied | unavailable
  const [errorText, setErrorText] = useState("");

  const stop = useCallback(() => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
    } catch { /* ignore */ }
    setStatus("idle");
  }, []);

  const start = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setStatus("unavailable");
      setErrorText("This browser can't reach the camera. Try Chrome or Safari.");
      return;
    }
    setStatus("requesting");
    setErrorText("");
    let stream = null;
    let lastErr = null;
    for (const c of CONSTRAINT_TIERS) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(c);
        if (stream) break;
      } catch (e) { lastErr = e; }
    }
    if (!stream) {
      const name = lastErr?.name || "";
      setStatus(name === "NotAllowedError" || name === "PermissionDeniedError" ? "denied" : "unavailable");
      setErrorText(
        name === "NotAllowedError" || name === "PermissionDeniedError"
          ? "Camera blocked. Enable it in your browser's site settings, then retry."
          : "No camera reachable. Are you on HTTPS?"
      );
      return;
    }
    streamRef.current = stream;
    const v = videoRef.current;
    if (!v) return;
    v.srcObject = stream;
    v.muted = true;
    v.playsInline = true;
    try {
      await v.play();
    } catch { /* browsers occasionally need a tap */ }
    setStatus("live");
  }, []);

  // Auto-start when the modal opens; auto-stop on close.
  useEffect(() => {
    if (open) start();
    return () => stop();
  }, [open, start, stop]);

  const snap = () => {
    const v = videoRef.current;
    if (!v || v.readyState < 2) return;
    const w = v.videoWidth || 640;
    const h = v.videoHeight || 480;
    const scale = Math.min(1, 1600 / Math.max(w, h));
    const cw = Math.round(w * scale);
    const ch = Math.round(h * scale);
    const canvas = document.createElement("canvas");
    canvas.width = cw; canvas.height = ch;
    canvas.getContext("2d").drawImage(v, 0, 0, cw, ch);
    const url = canvas.toDataURL("image/jpeg", 0.88);
    stop();
    onCapture?.(url);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[85] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3"
      data-testid="camera-capture-modal"
    >
      <div className="relative w-full max-w-md bg-neutral-950 border border-white/15 rounded-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Snap</div>
            <div className="text-sm font-bold text-white truncate">{title || "Capture item"}</div>
          </div>
          <button
            type="button"
            onClick={() => { stop(); onClose?.(); }}
            className="p-1.5 rounded bg-black/60 border border-white/20 hover:bg-white/10"
            aria-label="Close"
            data-testid="camera-capture-close"
          >
            <X size={14} className="text-white" />
          </button>
        </div>

        {/* Viewport */}
        <div className="relative bg-black" style={{ aspectRatio: "3 / 4" }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`w-full h-full object-cover ${status === "live" ? "opacity-100" : "opacity-30"}`}
            data-testid="camera-capture-video"
          />
          {status === "requesting" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/80">
              <Loader2 size={22} className="animate-spin" />
              <div className="text-xs">Turning on the camera…</div>
            </div>
          )}
          {(status === "denied" || status === "unavailable") && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-amber-200 p-4 text-center">
              <AlertCircle size={22} />
              <div className="text-xs">{errorText}</div>
              <button
                type="button"
                onClick={start}
                className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 text-black text-[10px] font-black uppercase tracking-widest"
                data-testid="camera-capture-retry"
              >
                <RefreshCw size={11} /> Retry
              </button>
            </div>
          )}
          {status === "live" && (
            <div className="pointer-events-none absolute inset-x-0 top-0 p-2 text-center">
              {hint && (
                <div className="inline-block px-2 py-1 rounded bg-black/60 text-[10px] text-white/85">
                  {hint}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Snap bar */}
        <div className="p-3 flex items-center justify-center">
          <button
            type="button"
            onClick={snap}
            disabled={status !== "live"}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black font-black text-sm uppercase tracking-widest disabled:opacity-40 hover:scale-105 transition"
            data-testid="camera-capture-snap"
          >
            <Camera size={16} /> Snap
          </button>
        </div>
      </div>
    </div>
  );
}

export default CameraCapture;
